import React from 'react';

import { useGetStream } from '@vlabs/api/artemy/client';
import { StreamTargetsParameterItem } from '@vlabs/api/artemy/model';
import type { StreamId } from '@vlabs/api/artemy/model';
import { useStreamIdFromLocation } from '@vlabs/monokit/shared/lib/use-stream-id-from-location';
import { isStreamTypeRestreamable } from '@vlabs/monokit/shared/lib/video';
import { Page, PageContent, PageSidePanel } from '@vlabs/monokit/shared/ui/page';
import { SidePanelProvider } from '@vlabs/monokit/shared/ui/side-panel';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import { Link } from 'react-router-dom';

import { LiveEventsPanel } from './components/LiveEventsPanel';
import { StreamErrorStub } from './components/StreamErrorStub';
import { StreamPlayer } from './components/StreamPlayer';
import { useSourceLivePageTranslation } from './i18n';
import { RestreamProvider, useRestream } from './restream-context';
import st from './SourceLivePage.module.sass';
import { SourceLivePageHeader } from './SourceLivePageHeader';
import { routes } from '../../app/routes';

interface SourceLivePageContentProps {
  streamId: StreamId;
}

function SourceLivePageContent({
  streamId,
}: SourceLivePageContentProps): React.JSX.Element {
  const { t } = useSourceLivePageTranslation();
  const { status, retransmission } = useRestream();

  const getStream = useGetStream(streamId, {
    targets: [StreamTargetsParameterItem.data],
  });
  const stream = getStream.data;

  if (getStream.isLoading) {
    return (
      <Page>
        <PageContent>
          <Loader />
        </PageContent>
      </Page>
    );
  }

  if (getStream.isError || !stream) {
    return (
      <Page>
        <PageContent>
          <div className={st.ErrorBlock}>
            <span>{t('load_error')}</span>
            <Link to={routes.sources.list}>{t('back_to_list')}</Link>
          </div>
        </PageContent>
      </Page>
    );
  }

  let streamContent: React.JSX.Element;
  if (!isStreamTypeRestreamable(stream.data?.type)) {
    streamContent = <StreamErrorStub />;
  } else if (status === 'error') {
    streamContent = <StreamErrorStub withReconnect />;
  } else if (!retransmission) {
    streamContent = (
      <div className={st.LoaderWrap}>
        <Loader />
      </div>
    );
  } else {
    streamContent = (
      <StreamPlayer />
    );
  }

  return (
    <Page>
      <PageContent className={st.PageContent}>
        <SourceLivePageHeader streamId={streamId} />

        <div className={st.StreamArea}>
          {streamContent}
        </div>
      </PageContent>

      {/* Панель событий не зависит от ретрансляции; всегда в ряду, не поверх контента */}
      <PageSidePanel
        className={st.EventsPanel}
        overlay={false}
      >
        <LiveEventsPanel streamId={streamId} />
      </PageSidePanel>
    </Page>
  );
}

function SourceLivePage(): React.JSX.Element {
  const streamId = useStreamIdFromLocation();

  return (
    <RestreamProvider streamId={streamId}>
      {/* По макету панель событий развернута по умолчанию */}
      <SidePanelProvider defaultOpen>
        <SourceLivePageContent streamId={streamId} />
      </SidePanelProvider>
    </RestreamProvider>
  );
}

export { SourceLivePage };
